import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ClockCounterClockwise, GitBranch, Plus, Eye, Star } from "@phosphor-icons/react";

export default function VersionsTab({ formulaId }) {
    const navigate = useNavigate();
    const [versions, setVersions] = useState(null);
    const [busy, setBusy] = useState(null);

    useEffect(() => {
        api.get(`/formulas/${formulaId}/versions`).then((r) => setVersions(r.data)).catch(() => setVersions([]));
    }, [formulaId]);

    const restore = async (vid, asNew) => {
        setBusy(vid + (asNew ? "-n" : "-v"));
        try {
            const { data } = await api.post(`/formulas/${formulaId}/versions/${vid}/restore`, { as_new_formula: asNew });
            toast.success(asNew ? "Versión rescatada como fórmula nueva" : `Versión rescatada como v${data.version}`);
            navigate(`/formulas/${data.id}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "No se pudo rescatar la versión");
        } finally { setBusy(null); }
    };

    if (!versions) return <div className="text-sm text-[var(--ink-3)]">Cargando historial…</div>;

    return (
        <div className="max-w-3xl" data-testid="versions-tab">
            <p className="text-sm text-[var(--ink-2)] mb-5 max-w-xl">
                Cronograma completo de versiones de esta fórmula. Cualquier versión puede <strong>rescatarse</strong>:
                como nueva versión dentro del desarrollo actual, o como fórmula independiente.
            </p>
            <div className="space-y-0">
                {versions.map((v, i) => (
                    <div key={v.id} className={`relative pl-8 pb-6 ${i < versions.length - 1 ? "border-l hairline ml-3" : "ml-3"}`} data-testid={`version-item-${v.version}`}>
                        <div className={`absolute -left-3 top-0 w-6 h-6 flex items-center justify-center text-[10px] mono border hairline ${v.is_current ? "bg-[var(--sage)] text-white border-[var(--sage)]" : "bg-white"}`}>
                            v{v.version}
                        </div>
                        <div className={`border p-4 ${v.is_most_significant ? "border-[var(--sage)] bg-[#E8F1E9]" : v.is_current ? "hairline bg-[var(--cream)]" : "hairline bg-white"}`}>
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        {v.is_current ? (
                                            <span className="font-display text-lg tracking-tight">{v.title}</span>
                                        ) : (
                                            <Link to={`/formulas/${v.id}`} className="font-display text-lg tracking-tight hover:text-[var(--sage)]">{v.title}</Link>
                                        )}
                                        {v.is_current && <span className="citation-badge bg-[var(--sage)] text-white border-[var(--sage)]">ACTUAL</span>}
                                        {v.is_most_significant && <span className="citation-badge bg-[var(--sage)] text-white border-[var(--sage)] inline-flex items-center gap-1" data-testid={`version-significant-${v.version}`}><Star size={11} weight="fill"/> CAMBIO MÁS SIGNIFICATIVO</span>}
                                        {v.status === "approved" && <span className="citation-badge">Aprobada</span>}
                                        {v.deleted && <span className="citation-badge text-[var(--terra-2)]">En papelera</span>}
                                    </div>
                                    <div className="text-xs mono text-[var(--ink-3)] mt-1">{(v.created_at || "").slice(0, 16).replace("T", " · ")}</div>
                                </div>
                            </div>
                            {v.change_summary && (
                                <p className="text-sm mt-2 text-[var(--ink-2)] border-l-2 border-[var(--sage)] pl-2.5">{v.change_summary}</p>
                            )}
                            {v.diff && !v.diff.is_origin && (
                                (v.diff.added?.length || v.diff.removed?.length || v.diff.dose_changed?.length) ? (
                                    <div className="flex flex-wrap gap-1.5 mt-2.5" data-testid={`version-diff-${v.version}`}>
                                        {v.diff.added?.map((n, k) => (
                                            <span key={`a${k}`} className="text-[11px] px-2 py-0.5 bg-[#E8F1E9] border border-[var(--sage)] text-[var(--sage)]">+ {n}</span>
                                        ))}
                                        {v.diff.removed?.map((n, k) => (
                                            <span key={`r${k}`} className="text-[11px] px-2 py-0.5 bg-[#FBEAE9] border border-[var(--terra-2)] text-[var(--terra-2)] line-through">− {n}</span>
                                        ))}
                                        {v.diff.dose_changed?.map((d, k) => (
                                            <span key={`d${k}`} className="text-[11px] px-2 py-0.5 bg-white border hairline text-[var(--ink-2)] mono">{d.name}: {d.from}→{d.to}{d.unit}</span>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-[11px] text-[var(--ink-3)] mt-2">Sin cambios de composición (ajustes de texto/análisis)</p>
                                )
                            )}
                            {v.diff?.is_origin && (
                                <p className="text-[11px] text-[var(--ink-3)] mt-2" data-testid={`version-origin-${v.version}`}>Versión original</p>
                            )}
                            {(v.components || []).length > 0 && (
                                <div className="text-xs mono text-[var(--ink-2)] mt-2">
                                    {v.components.map((c) => `${c.name} ${c.dose_mg}${c.unit || "mg"}`).join(" · ")}
                                </div>
                            )}
                            {!v.is_current && (
                                <div className="flex items-center gap-2 flex-wrap mt-3">
                                    <Link to={`/formulas/${v.id}`} className="px-2.5 py-1.5 border hairline bg-white text-xs hover:bg-[var(--cream)] inline-flex items-center gap-1" data-testid={`version-view-${v.version}`}>
                                        <Eye size={12}/> Ver
                                    </Link>
                                    <button onClick={() => restore(v.id, false)} disabled={!!busy}
                                        className="px-2.5 py-1.5 border hairline bg-white text-xs hover:bg-[var(--cream)] inline-flex items-center gap-1 disabled:opacity-50"
                                        data-testid={`version-restore-${v.version}`}>
                                        <GitBranch size={12}/> {busy === v.id + "-v" ? "Rescatando…" : "Rescatar como nueva versión"}
                                    </button>
                                    <button onClick={() => restore(v.id, true)} disabled={!!busy}
                                        className="px-2.5 py-1.5 border hairline bg-white text-xs hover:bg-[var(--cream)] inline-flex items-center gap-1 disabled:opacity-50"
                                        data-testid={`version-restore-new-${v.version}`}>
                                        <Plus size={12}/> {busy === v.id + "-n" ? "Creando…" : "Como fórmula nueva"}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
            {versions.length <= 1 && (
                <div className="border hairline bg-white p-5 text-sm text-[var(--ink-3)] mt-2 flex items-center gap-2" data-testid="versions-single">
                    <ClockCounterClockwise size={16}/> Esta es la única versión. Las nuevas versiones se crean desde el «Diálogo IA» o el botón «Nueva versión».
                </div>
            )}
        </div>
    );
}
